/**
 * c-theme.js — Unified Splide controller for all carousels.
 *
 * Handles three slider types via CSS class selectors:
 *   .c-splide-superfluid  — Superfluid hero sliders
 *   .c-splide-collection  — Featured collection carousels
 *   .splide[data-desktop-slides] — Generic Slider section/block (via splide-slider snippet)
 *
 * Loaded once via <script defer> in theme.liquid.
 * All config is read from data-* attributes on the .splide element.
 */

(function () {
  'use strict';

  var INITIALIZED = 'data-splide-initialized';
  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Gap token → px map
  var GAP_MAP = { none: 0, '2xs': 8, xs: 16, sm: 20, md: 24, lg: 28, xl: 32, '2xl': 36 };

  // ── Helpers ──────────────────────────────────────────────────────────

  function resolveGap(v) {
    if (!v || v === 'none') return '0px';
    if (GAP_MAP[v] !== undefined) return GAP_MAP[v] + 'px';
    return v.indexOf('px') > -1 ? v : v + 'px';
  }

  function toPercent(v) {
    if (!v || v === '0' || v === '0%') return '0%';
    return v.indexOf('%') > -1 ? v : v + '%';
  }

  function int(v, fallback) {
    var n = parseInt(v, 10);
    return isNaN(n) ? fallback : n;
  }

  function bool(v) {
    return v === 'true' || v === true;
  }

  // ── Accessibility setup (shared by all slider types) ────────────────

  function setupAccessibility(el, instance) {
    var slideList = el.querySelector('.splide__list');
    var slides = el.querySelectorAll('.splide__slide');
    var totalSlides = slides.length;

    if (slideList) slideList.setAttribute('role', 'list');

    for (var i = 0; i < slides.length; i++) {
      slides[i].setAttribute('role', 'listitem');
      slides[i].setAttribute('aria-roledescription', 'slide');
      slides[i].setAttribute('aria-label', 'Slide ' + (i + 1) + ' of ' + totalSlides);
    }

    // Live region (create once)
    if (!el.querySelector('.splide__sr-live')) {
      var liveRegion = document.createElement('div');
      liveRegion.className = 'splide__sr-live';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0);';
      el.appendChild(liveRegion);
    }

    // Announce slide changes
    instance.on('moved', function (newIndex) {
      var region = el.querySelector('.splide__sr-live');
      if (region) region.textContent = 'Slide ' + (newIndex + 1) + ' of ' + totalSlides;
    });
  }

  // ── Destroy helper ──────────────────────────────────────────────────

  function destroySlider(el) {
    if (!el) return;
    if (el._splideInstance) {
      el._splideInstance.destroy(true);
      delete el._splideInstance;
    }
    el.removeAttribute(INITIALIZED);
  }

  // ── Superfluid Slider ───────────────────────────────────────────────

  function initSuperfluid(el) {
    if (!el || el.hasAttribute(INITIALIZED)) return;

    var d = el.dataset;
    var instance = new Splide(el, {
      keyboard: 'focused',
      type: bool(d.infinite) ? 'loop' : 'slide',
      perPage: int(d.slidesDesktop, 1),
      perMove: int(d.scrollDesktop, 1),
      autoplay: bool(d.autoplay) && !REDUCED_MOTION,
      interval: int(d.autoplaySpeed, 3000),
      pauseOnHover: true,
      pauseOnFocus: true,
      speed: REDUCED_MOTION ? 0 : 400,
      arrows: bool(d.arrows),
      pagination: bool(d.dots),
      drag: bool(d.draggable),
      autoWidth: bool(d.freescroll),
      heightRatio: bool(d.adaptiveHeight) ? 0.5 : null,
      slideRole: '',
      breakpoints: {
        768: {
          perPage: int(d.slidesMobile, 1),
          perMove: int(d.scrollMobile, 1)
        }
      }
    });

    instance.on('mounted', function () {
      setupAccessibility(el, instance);

      // Hide arrows/dots when only 1 visible slide (unpublished slides render as empty <li>)
      var slides = el.querySelectorAll('.splide__slide');
      var visibleSlides = [];
      for (var i = 0; i < slides.length; i++) {
        if (slides[i].children.length > 0) visibleSlides.push(slides[i]);
      }
      if (visibleSlides.length === 0) {
        // Every slide is scheduled out — hide the empty carousel entirely.
        // In the theme editor slides bypass scheduling, so this won't trigger.
        el.style.display = 'none';
      } else if (visibleSlides.length <= 1) {
        var arrows = el.querySelector('.splide__arrows');
        var pagination = el.querySelector('.splide__pagination');
        if (arrows) arrows.style.display = 'none';
        if (pagination) pagination.style.display = 'none';
        if (instance.options.type === 'loop') {
          instance.options = { type: 'slide' };
        }
      }
    });

    instance.mount();
    el.setAttribute(INITIALIZED, 'true');
    el._splideInstance = instance;
  }

  function initAllSuperfluid() {
    var els = document.querySelectorAll('.c-splide-superfluid:not([' + INITIALIZED + '])');
    for (var i = 0; i < els.length; i++) initSuperfluid(els[i]);
  }

  // ── Featured Collection Slider ──────────────────────────────────────

  function initCollection(el) {
    if (!el || el.hasAttribute(INITIALIZED)) return;

    var d = el.dataset;
    var desktopPerPage = int(d.desktopPerPage, 4);
    var mobilePerPage = int(d.mobilePerPage, 1);
    var dPeekLeft = toPercent(d.desktopPeekLeft || '0');
    var dPeekRight = toPercent(d.desktopPeekRight || '10');
    var mPeekLeft = toPercent(d.mobilePeekLeft || '0');
    var mPeekRight = toPercent(d.mobilePeekRight || '15');

    var instance = new Splide(el, {
      perPage: desktopPerPage,
      perMove: 1,
      gap: '10px',
      pagination: false,
      speed: REDUCED_MOTION ? 0 : 600,
      drag: 'free',
      snap: true,
      slideRole: '',
      padding: { left: dPeekLeft, right: dPeekRight },
      breakpoints: {
        1280: {
          perPage: Math.min(desktopPerPage, 2)
        },
        768: {
          arrows: false,
          perPage: mobilePerPage,
          padding: { left: mPeekLeft, right: mPeekRight }
        }
      }
    });

    instance.on('mounted', function () {
      setupAccessibility(el, instance);
    });

    instance.mount();
    el.setAttribute(INITIALIZED, 'true');
    el._splideInstance = instance;
  }

  function initAllCollections() {
    var els = document.querySelectorAll('.c-splide-collection:not([' + INITIALIZED + '])');
    for (var i = 0; i < els.length; i++) initCollection(els[i]);
  }

  // ── Generic Slider (section/block via splide-slider snippet) ────────

  function initGenericSlider(el) {
    if (!el || el.hasAttribute(INITIALIZED)) return;
    // Skip product carousels and elements without data attributes
    if (el.classList.contains('product-carousel-splide') || !el.dataset.desktopSlides) return;
    // Skip elements handled by other init functions
    if (el.classList.contains('c-splide-superfluid') || el.classList.contains('c-splide-collection')) return;

    var d = el.dataset;
    var desktopSlides = int(d.desktopSlides, 1);
    var mobileSlides = int(d.mobileSlides, 1);
    var gap = resolveGap(d.gap || d.desktopGap);
    var mobileGap = resolveGap(d.mobileGap || d.gap || d.desktopGap);
    var infinite = bool(d.infinite);
    var autoplay = bool(d.autoplay);

    var padLeft = toPercent(d.desktopPeekLeft || d.desktopPeek || '0');
    var padRight = toPercent(d.desktopPeekRight || d.desktopPeek || '0');
    var mobPadLeft = toPercent(d.mobilePeekLeft || d.mobilePeek || '0');
    var mobPadRight = toPercent(d.mobilePeekRight || d.mobilePeek || '0');

    var hasPadding = padLeft !== '0%' || padRight !== '0%';
    var hasMobilePadding = mobPadLeft !== '0%' || mobPadRight !== '0%';

    var config = {
      type: infinite ? 'loop' : 'slide',
      perPage: desktopSlides,
      perMove: int(d.desktopScroll, 1),
      autoplay: autoplay && !REDUCED_MOTION,
      interval: int(d.autoplaySpeed, 3000),
      arrows: bool(d.arrows),
      pagination: bool(d.dots),
      drag: true,
      flickPower: 300,
      speed: REDUCED_MOTION ? 0 : 400,
      gap: gap,
      trimSpace: !infinite,
      slideRole: '',
      i18n: {
        prev: 'Previous slide',
        next: 'Next slide',
        first: 'Go to first slide',
        last: 'Go to last slide',
        slideX: 'Go to slide %s',
        pageX: 'Go to page %s'
      },
      breakpoints: {
        768: {
          perPage: mobileSlides,
          perMove: int(d.mobileScroll, 1),
          gap: mobileGap
        }
      }
    };

    if (hasPadding) config.padding = { left: padLeft, right: padRight };
    if (hasMobilePadding) config.breakpoints[768].padding = { left: mobPadLeft, right: mobPadRight };

    var instance = new Splide(el, config);

    instance.on('mounted', function () {
      setupAccessibility(el, instance);
    });

    instance.mount();
    el.setAttribute(INITIALIZED, 'true');
    el._splideInstance = instance;
  }

  function initAllGenericSliders() {
    var els = document.querySelectorAll('.splide[data-desktop-slides]:not([' + INITIALIZED + '])');
    for (var i = 0; i < els.length; i++) initGenericSlider(els[i]);
  }

  // ── Init all ────────────────────────────────────────────────────────

  function initAll() {
    initAllSuperfluid();
    initAllCollections();
    initAllGenericSliders();
  }

  // DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // ── Shopify Theme Editor support ────────────────────────────────────

  if (window.Shopify && window.Shopify.designMode) {

    document.addEventListener('shopify:section:load', function (e) {
      // Destroy any stale instances in the re-rendered section, then init fresh
      var sliders = e.target.querySelectorAll('.splide[' + INITIALIZED + ']');
      for (var i = 0; i < sliders.length; i++) destroySlider(sliders[i]);
      initAll();
    });

    document.addEventListener('shopify:section:unload', function (e) {
      var sliders = e.target.querySelectorAll('.splide[' + INITIALIZED + ']');
      for (var i = 0; i < sliders.length; i++) destroySlider(sliders[i]);
    });

    document.addEventListener('shopify:block:select', function (e) {
      var slide = e.target.closest('.splide__slide');
      if (!slide) return;
      var splideEl = slide.closest('.splide');
      var instance = splideEl && splideEl._splideInstance;
      if (!instance) return;

      var index = Array.from(splideEl.querySelectorAll('.splide__slide')).indexOf(slide);
      if (index >= 0) instance.go(index);
      if (instance.Components && instance.Components.Autoplay) {
        instance.Components.Autoplay.pause();
      }
    });

    document.addEventListener('shopify:block:deselect', function (e) {
      var slide = e.target.closest('.splide__slide');
      if (!slide) return;
      var instance = slide.closest('.splide');
      instance = instance && instance._splideInstance;
      if (instance && instance.Components && instance.Components.Autoplay) {
        instance.Components.Autoplay.play();
      }
    });
  }

  // Lazy-init for tabbed panels
  document.addEventListener('splide:lazy-init', function (e) {
    var el = e.target;
    if (el && el.matches && el.matches('.splide')) {
      if (el.classList.contains('c-splide-superfluid')) initSuperfluid(el);
      else if (el.classList.contains('c-splide-collection')) initCollection(el);
      else initGenericSlider(el);
    }
  });

})();
